import type { CategoryViewProps, CategoryProps } from '../../types';

import * as React from 'react';
import styled from '@emotion/styled';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useInView } from 'react-intersection-observer';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'next-i18next';
import { useHighlightHandlers } from '../../contexts/HighlightContext/Highlight';
import { Typography as MuiTypography, useMediaQuery } from '@mui/material';
import Container from '@mui/material/Container';
import { useAppContext } from '../../contexts/AppContext';
import { adsRequest } from '../../utils/ads-request';
import { Card, CardSkeleton } from '../../components/Card';
import { Title } from '../../components/Title';
import { PaperColorElevation } from '../../components/PaperColorElevation';
import { BreadCrumbs } from '../../components/BreadCrumbs';
import { CardTypeEnum } from '../../types/enum';
import { useCursorContext } from '../../contexts/CursorContext';

const AdvertisementSlot = dynamic<any>(
  () => import('../../components/AdvertisementSlot').then((mod) => mod.AdvertisementSlot),
  {
    ssr: false,
  }
);
const CollectionSection = dynamic<any>(
  () => import('../../components/CollectionSection').then((mod) => mod.CollectionSection),
  {
    ssr: false,
  }
);
const CarouselList = dynamic<any>(() => import('../../components/CarouselList').then((mod) => mod.CarouselList), {
  ssr: false,
});

interface CollectionBoxProps {
  box: number;
}

const Typography = React.forwardRef<HTMLInputElement, any>((props, ref) =>
  useHighlightHandlers(MuiTypography, { forwardedRef: ref, ...props })
);

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  paddingBottom: theme.spacing(2),
}));

const AdCollectionContainer = styled('div')(({ theme }) => ({
  display: 'grid',
  padding: theme.spacing(1, 2, 0),
  gap: theme.spacing(2),
  gridTemplateColumns: 'auto',
  gridTemplateAreas: `
        'advertisement advertisement'
        'gameA gameB'`,
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'auto 512px',
    gridTemplateAreas: `
        'advertisement gameA'
        'advertisement gameB'`,
  },
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(0, 2, 0),
    gridTemplateColumns: `calc(970px + ${theme.spacing(2)}) 1fr`,
    gridTemplateAreas: `
        'advertisement gameA'
        'advertisement gameB'`,
  },
  [theme.breakpoints.up(1600)]: {
    gridTemplateColumns: 'auto 512px',
  },
  [theme.breakpoints.up('xl')]: {
    gridTemplateColumns: `calc(970px + ${theme.spacing(2)}) 1fr 1fr`,
    gridTemplateAreas: `
        'advertisement gameA gameB'`,
  },
}));

const AdvertisementSlotStyled = styled(AdvertisementSlot)(({ theme }) => ({
  boxShadow: theme.shadows[0],
}));

const CardSkeletonStyled = styled(CardSkeleton)(({ theme }) => ({
  boxShadow: theme.shadows[0],
}));

const CardSkeletonGrid = styled(CardSkeleton)(({ theme }) => ({
  boxShadow: theme.shadows[0],
  aspectRatio: '4/3',
}));

const AdCollectionBox = styled(PaperColorElevation)<CollectionBoxProps>((props) => {
  const { theme, box } = props;
  let styles = {};
  switch (box) {
    case 1:
      styles = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        gridArea: 'advertisement',
        boxShadow: theme.shadows[0],
        [theme.breakpoints.up(370)]: {
          // Making sure 336x280 ad will fit, and after we have a nice padding.
          padding: theme.spacing(2),
        },
      };
      break;
    case 2:
      styles = {
        gridArea: 'gameA',
        boxShadow: theme.shadows[0],
      };
      break;
    case 3:
      styles = {
        gridArea: 'gameB',
        boxShadow: theme.shadows[0],
      };
      break;
  }

  return styles;
});

const CollectionContainer = styled('div')(({ theme }) => ({
  display: 'grid',
  padding: theme.spacing(0, 2, 1),
  gap: theme.spacing(2),
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gridTemplateRows: 'auto',
  gridTemplateAreas: `
        'A A'
        'B C'
        'D D'
        'E F'
        'G G'
        'H I'
        'J K'`,
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gridTemplateRows: 'auto',
    gridTemplateAreas: `
        'A A B C'
        'A A E F'
        'H I D D'
        'J K D D'
        'G G . .'`,
  },
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(10, minmax(0, 1fr))',
    gridTemplateRows: 'repeat(2, 1fr)',
    gridTemplateAreas: `
        'A A B C D D E F G G'
        'A A H I D D J K G G'`,
  },
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(2),
  },
}));

const CollectionBox = styled('div')<CollectionBoxProps>((props) => {
  const { box } = props;
  let styles = {};
  switch (box) {
    case 1:
      styles = { gridArea: 'A' };
      break;
    case 2:
      styles = { gridArea: 'B' };
      break;
    case 3:
      styles = { gridArea: 'C' };
      break;
    case 4:
      styles = { gridArea: 'D' };
      break;
    case 5:
      styles = { gridArea: 'E' };
      break;
    case 6:
      styles = { gridArea: 'F' };
      break;
    case 7:
      styles = { gridArea: 'G' };
      break;
    case 8:
      styles = { gridArea: 'H' };
      break;
    case 9:
      styles = { gridArea: 'I' };
      break;
    case 10:
      styles = { gridArea: 'J' };
      break;
    case 11:
      styles = { gridArea: 'K' };
      break;
  }

  return styles;
});

const HeaderTop = styled(Container)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const HeaderBottom = styled(Container)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

export const CollectionA = (props: CategoryViewProps) => {
  const { title, body, collection, global, pageContext } = props;
  const { t } = useTranslation('collection_a');
  const { ref, inView } = useInView();
  const router = useRouter();
  const appContext = useAppContext();
  const { enabled } = useCursorContext();
  const { assetUrl, componentOptions } = appContext.useConfig();
  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.up('sm'));
  const lg = useMediaQuery(theme.breakpoints.up('lg'));
  const banners = ['idhb-middle-970'];
  const descriptionTopRef = React.useRef<HTMLInputElement | null>(null);
  const descriptionBottomRef = React.useRef<HTMLInputElement | null>(null);

  const contentMainSection = [...Array(11)].map((slot, index) => collection.games?.[index]);
  const contentAdSection = [...Array(2)].map(
    (slot, index) => collection.games?.slice(contentMainSection.length, contentMainSection.length + 2)?.[index]
  );
  const contentMoreSection = collection.games?.slice(contentAdSection.length + contentMainSection.length) || [];
  const cardType = componentOptions?.component_game_carousel_card_type;

  React.useEffect(() => {
    adsRequest(banners);
  }, []);

  React.useEffect(() => {
    const { events } = router;
    const handleRouteChange = () => {
      adsRequest(banners);
    };

    events.on('routeChangeComplete', handleRouteChange);
    events.on('hashChangeComplete', handleRouteChange);
    return () => {
      events.off('routeChangeComplete', handleRouteChange);
      events.off('hashChangeComplete', handleRouteChange);
    };
  }, [router]);

  return (
    <>
      <Container maxWidth={false}>
        <BreadCrumbs breadcrumbs={pageContext.breadcrumbs} hidden={componentOptions?.component_breadcrumb_hidden} />
      </Container>
      <Root ref={ref}>
        <HeaderTop maxWidth={false}>
          {title && (
            <Title component="h1" variant="h1" disableGutters>
              {title}
            </Title>
          )}
          {body && (
            <Typography
              ref={descriptionTopRef}
              variant="body1"
              sx={{ marginTop: theme.spacing(1.25), marginBottom: theme.spacing(1) }}
            >
              {body}
            </Typography>
          )}
        </HeaderTop>
        <CollectionContainer>
          {contentMainSection.map((item, index) => (
            <CollectionBox key={index} box={index}>
              {item ? (
                <Card
                  sx={{ display: 'flex' }}
                  href={`${enabled ? '#' : `/game/${item.slug}`}`}
                  src={`${assetUrl}${
                    [1, 4, 7].includes(index)
                      ? item.images.cover4x3 || item.images.cover
                      : item.images.cover16x9 || item.images.cover
                  }/`}
                  alt={item.name || 'Cover'}
                  template={CardTypeEnum.SIMPLE}
                  title={item.name || undefined}
                  width={[1, 4, 7].includes(index) ? 400 : componentOptions.component_card_width || 512}
                  height={[1, 4, 7].includes(index) ? 300 : componentOptions.component_card_height || 288}
                  sizes={
                    [1, 4, 7].includes(index)
                      ? `${theme.breakpoints.up('md').replace('@media ', '')} 20vw, ${theme.breakpoints
                          .up('sm')
                          .replace('@media ', '')} 50vw, 100vw`
                      : `${theme.breakpoints.up('md').replace('@media ', '')} 10vw, ${theme.breakpoints
                          .up('sm')
                          .replace('@media ', '')} 25vw, 50vw`
                  }
                  priority={index === 1}
                  layout="responsive"
                  // typographyOptions={{
                  //   align: 'center',
                  // }}
                  // defaultAssetPath: sm && [1, 4, 7].includes(index + 1) ? 'cover' : 'desktop',
                />
              ) : (
                <CardSkeletonGrid elevation={1} />
              )}
            </CollectionBox>
          ))}
        </CollectionContainer>
        <AdCollectionContainer>
          {!componentOptions.component_ads_disabled && (
            <AdCollectionBox box={1} elevation={1}>
              <AdvertisementSlotStyled
                elevation={2}
                slotIdentifier="idhb-middle-970"
                variant={lg ? '970x250' : sm ? '336x280' : '300x250'}
              />
            </AdCollectionBox>
          )}
          {contentAdSection.map((game, index) => (
            <AdCollectionBox key={index} box={index + 2} elevation={0}>
              {game?.id ? (
                <Card
                  sx={{ display: 'flex' }}
                  href={`/game/${game.slug}`}
                  src={`${assetUrl}${game.images.cover16x9 || game.images.cover}/`}
                  alt={game.name || 'Cover'}
                  title={game.name || undefined}
                  template={CardTypeEnum.SIMPLE}
                  width={componentOptions.component_card_width || 512}
                  height={componentOptions.component_card_height || 288}
                  sizes={`${theme.breakpoints.up('lg').replace('@media ', '')} 20vw, 50vw`}
                  layout="responsive"
                  // defaultAssetPath: sm && [1, 4, 7].includes(index + 1) ? 'cover' : 'desktop',
                />
              ) : (
                <CardSkeletonStyled elevation={1} />
              )}
            </AdCollectionBox>
          ))}
        </AdCollectionContainer>
        {inView && contentMoreSection.length > 0 && (
          <CollectionSection>
            <Title component="h2" variant="h2">
              {t('more_header', 'More')}
            </Title>
            <CarouselList
              padding={theme.spacing(2)}
              items={contentMoreSection}
              cardOptions={{
                width: componentOptions.component_card_width || 512,
                height: componentOptions.component_card_height || 288,
                sizes: '(min-width:1200px) 16.666vw, (min-width:900px) 25vw, (min-width:600px) 33.333vw, 50vw',
                layout: 'responsive',
              }}
            />
          </CollectionSection>
        )}
        {inView &&
          (global?.collections || []).map(
            (collection: CategoryProps, index: number) =>
              collection.games.length > 0 && (
                <CollectionSection key={`${collection.id}${index}`}>
                  <Title component="h2" variant="h2">
                    {collection.name}
                  </Title>
                  <CarouselList
                    padding={theme.spacing(2)}
                    items={collection.games}
                    cardOptions={{
                      width: componentOptions.component_card_width || 512,
                      height: componentOptions.component_card_height || 288,
                      sizes: '(min-width:1200px) 16.666vw, (min-width:900px) 25vw, (min-width:600px) 33.333vw, 50vw',
                      layout: 'responsive',
                    }}
                  />
                </CollectionSection>
              )
          )}
        {inView &&
          Array.isArray(global.popular) && global.popular.length > 0 && (
            <CollectionSection>
              <Title component="h2" variant="h2">
                {t('popular_header', 'Popular')}
              </Title>
              <CarouselList
                padding={theme.spacing(2)}
                items={global.popular}
                cardOptions={{
                  width: componentOptions.component_card_width || 512,
                  height: componentOptions.component_card_height || 288,
                  sizes: '(min-width:1200px) 16.666vw, (min-width:900px) 25vw, (min-width:600px) 33.333vw, 50vw',
                  layout: 'responsive',
                }}
              />
            </CollectionSection>
          )}
        {inView && (
          <CollectionSection>
            <Title component="h2" variant="h2">
              {t('latest_header', 'Latest')}
            </Title>
            <CarouselList
              padding={theme.spacing(2)}
              items={global?.latest}
              cardOptions={{
                width: componentOptions.component_card_width || 512,
                height: componentOptions.component_card_height || 288,
                sizes: '(min-width:1200px) 16.666vw, (min-width:900px) 25vw, (min-width:600px) 33.333vw, 50vw',
                layout: 'responsive',
              }}
            />
          </CollectionSection>
        )}
        <HeaderBottom maxWidth={false} sx={{ marginTop: theme.spacing(2) }}>
          {title && (
            <Title component="h1" variant="h1" disableGutters>
              {title}
            </Title>
          )}
          {body && (
            <Typography ref={descriptionBottomRef} variant="body1" sx={{ marginTop: theme.spacing(1.25) }}>
              {body}
            </Typography>
          )}
        </HeaderBottom>
      </Root>
    </>
  );
};
